import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ShieldCheck, LockKey, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Mandatory 2FA setup page. All users must have 2FA enabled to use the app.
 * Users are redirected here from the Protected gate if totp_enabled=false.
 */
export default function Setup2faPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [status, setStatus] = useState({ loading: true, enabled: false });
  const [setupData, setSetupData] = useState(null); // {qr_code, secret}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/2fa/status");
        setStatus({ loading: false, enabled: !!data.enabled });
        // If already enabled, bounce back to intended destination
        if (data.enabled) {
          const dest = loc.state?.from || "/";
          nav(dest, { replace: true });
        }
      } catch {
        setStatus({ loading: false, enabled: false });
      }
    })();
  }, [nav, loc.state]);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.length !== 6) { toast.error("Inserisci il codice a 6 cifre"); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { totp_code: code });
      toast.success("2FA attivata! Accesso completato.");
      const dest = loc.state?.from || "/";
      nav(dest, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Codice non valido");
    } finally { setBusy(false); }
  };

  const doLogout = async () => { await logout(); nav("/login"); };

  if (status.loading) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Caricamento…</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/40 to-background p-6 flex items-center justify-center">
      <Card className="max-w-lg w-full p-8 border-border/70 rounded-lg" data-testid="setup-2fa-page">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-primary/15 text-primary grid place-items-center mx-auto mb-4">
            <ShieldCheck size={32} weight="duotone" />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">// SICUREZZA OBBLIGATORIA</div>
          <h1 className="text-2xl font-display font-bold tracking-tight mb-2">Attiva l'autenticazione a due fattori</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Per proteggere i dati del tuo gestionale, tutti gli utenti devono attivare il 2FA.
            <br />Non puoi accedere alle altre pagine finché non completi questo passaggio.
          </p>
          <div className="mt-3 text-xs text-muted-foreground">
            Utente: <span className="font-mono font-medium text-foreground">{user?.email}</span>
          </div>
        </div>

        {!setupData && (
          <div className="space-y-4">
            <div className="rounded-md bg-secondary/60 border border-border/50 p-4 text-sm">
              <div className="font-semibold mb-2 flex items-center gap-2"><LockKey size={14} /> Cosa ti serve</div>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground text-xs">
                <li>Un'app authenticator sul telefono (Google Authenticator, Microsoft Authenticator, Authy, 1Password…)</li>
                <li>1 minuto di tempo</li>
              </ul>
            </div>
            <Button onClick={startSetup} disabled={busy} data-testid="start-2fa-setup-btn" className="w-full rounded-full gap-2">
              <ShieldCheck size={16} weight="bold" /> {busy ? "Genero il QR…" : "Inizia setup 2FA"}
            </Button>
          </div>
        )}

        {setupData && (
          <div className="space-y-4">
            <div className="text-sm">
              <div className="font-semibold mb-2">1. Scansiona il QR code</div>
              <div className="flex justify-center">
                <img src={setupData.qr_code} alt="QR 2FA" className="w-48 h-48 border border-border rounded-md" data-testid="totp-qr-image" />
              </div>
            </div>

            <div className="text-sm">
              <div className="font-semibold mb-1">Oppure inserisci manualmente questa chiave:</div>
              <div className="font-mono text-xs bg-secondary/60 px-3 py-2 rounded break-all border border-border" data-testid="totp-secret">
                {setupData.secret}
              </div>
            </div>

            <div className="text-sm">
              <Label className="font-semibold mb-2 block">2. Inserisci il codice a 6 cifre dall'app</Label>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="totp-verify-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>

            <Button onClick={verify} disabled={busy || code.length !== 6} data-testid="verify-2fa-setup-btn" className="w-full rounded-full gap-2">
              <ShieldCheck size={16} weight="bold" /> {busy ? "Verifico…" : "Attiva 2FA e accedi"}
            </Button>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-border/50 text-center">
          <button onClick={doLogout} data-testid="logout-from-2fa-btn" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <SignOut size={12} /> Esci e collega più tardi
          </button>
        </div>
      </Card>
    </div>
  );
}
